import React, { useEffect, useState } from "react";
import Title from "../common/Title";
import Utils from "../../utils";
import { useLocation, useNavigate } from "react-router-dom";
import { Form, Button, Spinner, Alert } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { emailDomainAuthenticatorActions } from "../../actions/emailDomainAuthenticator.actions";
import { FaKey } from "react-icons/fa";
import SkeletonForm from "../common/SkeletonForm";
import { useToast } from "../../contexts/ToastContext";

function AuthenticatorForm({ presetAuthId, presetClientId, onSaveSuccess, onCancel, hideTitle }) {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [authId, setAuthId] = useState("");
  const [emailDomain, setEmailDomain] = useState("");
  const [clientId, setClientId] = useState("");
  const [authenticatorType, setAuthenticatorType] = useState("Okta");
  const [oktaClientId, setOktaClientId] = useState("");
  const [oktaIssuerUrl, setOktaIssuerUrl] = useState("");
  const [azureAdTenantId, setAzureAdTenantId] = useState("");
  const [azureAdClientId, setAzureAdClientId] = useState("");
  const [azureAdClientSecret, setAzureAdClientSecret] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const emailDomainAuthenticatorReducer = useSelector(
    (state) => state.emailDomainAuthenticator
  );

  useEffect(() => {
    let queryParams = Utils.getQueryStringParams(location);
    const idToLoad = presetAuthId ?? queryParams["id"];

    if (idToLoad) {
      setAuthId(idToLoad);
      dispatch(emailDomainAuthenticatorActions.getById({ ...queryParams, id: idToLoad }));
    } else {
      setEmailDomain("");
      setAuthenticatorType("Okta");
      setClientId(presetClientId ?? queryParams["clientId"]);
      setOktaClientId("");
      setOktaIssuerUrl("");
      setAzureAdTenantId("");
      setAzureAdClientId("");
      setAzureAdClientSecret("");
    }
    // eslint-disable-next-line
  }, [location, presetAuthId, presetClientId]);

  useEffect(() => {
    if (
      authId &&
      emailDomainAuthenticatorReducer &&
      emailDomainAuthenticatorReducer.byId
    ) {
      const auth = emailDomainAuthenticatorReducer.byId;
      setEmailDomain(auth.emailDomain);
      setAuthenticatorType(auth.authenticatorType);
      setClientId(auth.clientId);
      setOktaClientId(auth.oktaClientId || "");
      setOktaIssuerUrl(auth.oktaIssuerUrl || "");
      setAzureAdTenantId(auth.azureAdTenantId || "");
      setAzureAdClientId(auth.azureAdClientId || "");
      setAzureAdClientSecret(""); // Never populate secret from API for security
    }
    // eslint-disable-next-line
  }, [emailDomainAuthenticatorReducer]);

  const handleSaveAuthenticator = () => {
    setSubmitted(true);

    // Validate based on authenticator type
    let isValid = false;
    if (authenticatorType === "Okta") {
      isValid = emailDomain && oktaClientId && oktaIssuerUrl;
    } else if (authenticatorType === "AzureAD" || authenticatorType === "EntraID") {
      isValid = emailDomain && azureAdTenantId && azureAdClientId;
    }

    if (isValid) {
      // Build request body based on authenticator type
      const requestBody = {
        emailDomain,
        clientId,
        authenticatorType,
      };

      if (authenticatorType === "Okta") {
        requestBody.oktaClientId = oktaClientId;
        requestBody.oktaIssuerUrl = oktaIssuerUrl;
      } else if (authenticatorType === "AzureAD" || authenticatorType === "EntraID") {
        requestBody.azureAdTenantId = azureAdTenantId;
        requestBody.azureAdClientId = azureAdClientId;
        if (azureAdClientSecret) {
          requestBody.azureAdClientSecret = azureAdClientSecret;
        }
      }

      if (authId) {
        dispatch(
          emailDomainAuthenticatorActions.update({
            id: authId,
            ...requestBody,
          })
        );
      } else {
        dispatch(emailDomainAuthenticatorActions.create(requestBody));
      }
    }
  };

  useEffect(() => {
    if (emailDomainAuthenticatorReducer.success) {
      showToast("Authenticator saved successfully", "success");
      if (onSaveSuccess) {
        onSaveSuccess();
      } else {
        navigate(`/client/authenticators?clientId=${clientId}`);
      }
    }
    // eslint-disable-next-line
  }, [emailDomainAuthenticatorReducer.success]);

  const operation = !clientId ? "Add" : "Update";
  const loading = emailDomainAuthenticatorReducer.loading;

  return (
    <>
      {!hideTitle && (
        <Title
          text={`${operation} Email Domain Authenticator`}
          icon={<FaKey className="mb-1" />}
        />
      )}

      {loading && <SkeletonForm fields={4} />}

      {!loading && (
        <>
          {!emailDomainAuthenticatorReducer.success && (
            <>
              {emailDomainAuthenticatorReducer.error && (
                <Alert variant={"danger"}>
                  An error has occurred. Please try again later.
                </Alert>
              )}

              <Form.Group controlId="formEmailDomain" className="mb-3">
                <Form.Label>Email Domain</Form.Label>
                <Form.Control
                  type="text"
                  value={emailDomain}
                  onChange={(e) => setEmailDomain(e.target.value)}
                  className={submitted && !emailDomain ? "is-invalid" : ""}
                />
                <Form.Control.Feedback type="invalid">
                  <small>Please provide a valid email domain.</small>
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group controlId="formAuthenticatorType" className="mb-3">
                <Form.Label>Authenticator Type</Form.Label>
                <Form.Select
                  value={authenticatorType}
                  onChange={(e) => setAuthenticatorType(e.target.value)}
                >
                  <option value="Okta">Okta</option>
                  <option value="AzureAD">Microsoft Azure AD / Entra ID</option>
                </Form.Select>
              </Form.Group>

              {/* Okta Fields */}
              {authenticatorType === "Okta" && (
                <>
                  <Form.Group controlId="formOktaClientId" className="mb-3">
                    <Form.Label>Okta Client Id</Form.Label>
                    <Form.Control
                      type="text"
                      value={oktaClientId}
                      onChange={(e) => setOktaClientId(e.target.value)}
                      className={submitted && !oktaClientId ? "is-invalid" : ""}
                    />
                    <Form.Control.Feedback type="invalid">
                      <small>Please provide Okta Client Id.</small>
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group controlId="formOktaIssuerUrl" className="mb-3">
                    <Form.Label>Okta Issuer URL</Form.Label>
                    <Form.Control
                      type="text"
                      value={oktaIssuerUrl}
                      onChange={(e) => setOktaIssuerUrl(e.target.value)}
                      className={submitted && !oktaIssuerUrl ? "is-invalid" : ""}
                    />
                    <Form.Control.Feedback type="invalid">
                      <small>Please provide Okta Issuer URL.</small>
                    </Form.Control.Feedback>
                  </Form.Group>
                </>
              )}

              {/* Azure AD Fields */}
              {(authenticatorType === "AzureAD" || authenticatorType === "EntraID") && (
                <>
                  <Form.Group controlId="formAzureAdTenantId" className="mb-3">
                    <Form.Label>Azure AD Tenant ID</Form.Label>
                    <Form.Control
                      type="text"
                      value={azureAdTenantId}
                      onChange={(e) => setAzureAdTenantId(e.target.value)}
                      className={submitted && !azureAdTenantId ? "is-invalid" : ""}
                      placeholder="aaaaaaaa-bbbb-cccc-dddd-eeeeeeeeeeee"
                    />
                    <Form.Control.Feedback type="invalid">
                      <small>Please provide Azure AD Tenant ID.</small>
                    </Form.Control.Feedback>
                    <Form.Text className="text-muted">
                      Find this in Azure Portal under Azure Active Directory
                    </Form.Text>
                  </Form.Group>

                  <Form.Group controlId="formAzureAdClientId" className="mb-3">
                    <Form.Label>Azure AD Application (Client) ID</Form.Label>
                    <Form.Control
                      type="text"
                      value={azureAdClientId}
                      onChange={(e) => setAzureAdClientId(e.target.value)}
                      className={submitted && !azureAdClientId ? "is-invalid" : ""}
                      placeholder="ffffffff-gggg-hhhh-iiii-jjjjjjjjjjjj"
                    />
                    <Form.Control.Feedback type="invalid">
                      <small>Please provide Azure AD Application (Client) ID.</small>
                    </Form.Control.Feedback>
                    <Form.Text className="text-muted">
                      Find this in your App Registration in Azure Portal
                    </Form.Text>
                  </Form.Group>

                  <Form.Group controlId="formAzureAdClientSecret" className="mb-3">
                    <Form.Label>
                      Azure AD Client Secret {!authId && "(Optional)"}
                      {authId && " (Optional - Leave blank to keep existing)"}
                    </Form.Label>
                    <Form.Control
                      type="password"
                      value={azureAdClientSecret}
                      onChange={(e) => setAzureAdClientSecret(e.target.value)}
                      placeholder="Enter client secret if using confidential client"
                    />
                    <Form.Text className="text-muted">
                      Only required if using a confidential client application
                    </Form.Text>
                  </Form.Group>
                </>
              )}

              <Button
                variant="outline-secondary"
                type="button"
                className="me-3 mt-3"
                onClick={() => (onCancel ? onCancel() : navigate(-1))}
              >
                Cancel
              </Button>

              <Button
                variant="primary"
                type="submit"
                className="mt-3"
                onClick={handleSaveAuthenticator}
                disabled={loading}
              >
                {!loading ? "Save Authenticator" : <Spinner size="sm" />}
              </Button>
            </>
          )}
        </>
      )}
    </>
  );
}

export default AuthenticatorForm;
